import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs,
  startAfter, where, doc, updateDoc, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase';
import { PageHeader, Table, Badge, Card, StatCard,
  GoldBtn, TabButtons, Spinner } from '../components/UI';

const ACCENT = '#008751';
const GOLD = '#D4AF37';
const PAGE_SIZE = 20;

const ALERT_ICONS = {
  rate_limit_exceeded:  '⚡',
  daily_limit_exceeded: '📊',
  content_safety_flag:  '🚨',
  suspicious_activity:  '🔍',
  default:              '⚠',
};

export default function Safety() {
  const [alerts,   setAlerts]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [lastDoc,  setLastDoc]  = useState(null);
  const [hasMore,  setHasMore]  = useState(true);
  const [filter,   setFilter]   = useState('open');
  const [stats,    setStats]    = useState({});
  const [selected, setSelected] = useState(null);

  useEffect(() => { loadAlerts(true); }, [filter]);
  useEffect(() => { loadStats(); }, []);

  async function loadStats() {
    try {
      const col = collection(db,'security_alerts');
      const [open, reviewed, flagged] = await Promise.all([
        getCountFromServer(query(col, where('status','==','open'))),
        getCountFromServer(query(col, where('status','==','reviewed'))),
        getCountFromServer(query(col, where('status','==','flagged'))),
      ]);
      setStats({
        open:     open.data().count,
        reviewed: reviewed.data().count,
        flagged:  flagged.data().count,
      });
    } catch(e) { console.error(e); }
  }

  async function loadAlerts(reset=true) {
    setLoading(true);
    try {
      let q = query(collection(db,'security_alerts'),
        orderBy('createdAt','desc'), limit(PAGE_SIZE));
      if (filter !== 'all') q = query(q, where('status','==',filter));
      if (!reset && lastDoc) q = query(q, startAfter(lastDoc));

      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({id:d.id,...d.data()}));
      setAlerts(reset ? data : [...alerts,...data]);
      setLastDoc(snap.docs[snap.docs.length-1]||null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch(e) { console.error(e); }
    setLoading(false);
  }

  async function updateStatus(alertId, status) {
    try {
      await updateDoc(doc(db,'security_alerts',alertId), { status,
        reviewedAt: new Date() });
      setAlerts(prev => prev.map(a => a.id===alertId ? {...a,status} : a));
      if (filter !== 'all' && filter !== status) {
        setAlerts(prev => prev.filter(a => a.id !== alertId));
      }
      setStats(prev => ({...prev,
        [filter]: Math.max(0,(prev[filter]||0)-1),
        [status]: (prev[status]||0)+1,
      }));
      if (selected?.id === alertId) setSelected(prev => ({...prev,status}));
    } catch(e) { alert('Error: '+e.message); }
  }

  async function blockUser(uid) {
    if (!window.confirm('Block this user? They will be suspended from Oraku.')) return;
    try {
      await updateDoc(doc(db,'users',uid), { suspended: true, flagged: true });
      alert('User has been blocked');
    } catch(e) { alert('Error: '+e.message); }
  }

  async function unblockUser(uid) {
    try {
      await updateDoc(doc(db,'users',uid), { suspended: false, flagged: false });
      alert('User has been unblocked');
    } catch(e) { alert('Error: '+e.message); }
  }

  const fmtDate = ts => ts?.toDate
    ? ts.toDate().toLocaleString('en-GB',{day:'numeric',month:'short',
        hour:'2-digit',minute:'2-digit'})
    : '—';

  const COLUMNS = [
    { key:'alertType', label:'Type',
      render:(v,row) => (
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <div style={{width:36,height:36,borderRadius:10,
            background: v === 'content_safety_flag' ? 'rgba(239,68,68,0.1)' : 'rgba(212,175,55,0.08)',
            display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,
            border:`1px solid ${v === 'content_safety_flag' ? 'rgba(239,68,68,0.15)' : 'rgba(212,175,55,0.12)'}`,
            flexShrink:0}}>
            {ALERT_ICONS[v] || ALERT_ICONS.default}
          </div>
          <div>
            <div style={{fontSize:13,fontWeight:600,color:'#fff'}}>
              {v?.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}
            </div>
            <div style={{fontSize:11,color:'rgba(255,255,255,0.3)',marginTop:2}}>
              {row.email || row.uid?.substring(0,16)+'...'}
            </div>
          </div>
        </div>
      )},
    { key:'country', label:'Country', render: v => v || '—' },
    { key:'tier',    label:'Tier',    render: v => <Badge label={v||'free'} color={v||'free'} /> },
    { key:'status',  label:'Status',  render: v => <Badge label={v} color={v} /> },
    { key:'createdAt', label:'Time',  render: fmtDate },
    { key:'id', label:'Actions',
      render: (v,row) => (
        <div style={{display:'flex',gap:6}} onClick={e => e.stopPropagation()}>
          {row.status === 'open' && <>
            <GoldBtn onClick={() => updateStatus(v,'reviewed')} small>Review</GoldBtn>
            <GoldBtn onClick={() => updateStatus(v,'flagged')} small danger>Flag</GoldBtn>
          </>}
          {row.status === 'flagged' && (
            <GoldBtn onClick={() => updateStatus(v,'dismissed')} small>Dismiss</GoldBtn>
          )}
          {row.status === 'reviewed' && (
            <GoldBtn onClick={() => updateStatus(v,'dismissed')} small danger>Dismiss</GoldBtn>
          )}
        </div>
      )},
  ];

  const STATUS_TABS = [
    {value:'open',label:'Open',count:stats.open||0},
    {value:'reviewed',label:'Reviewed',count:stats.reviewed||0},
    {value:'flagged',label:'Flagged',count:stats.flagged||0},
    {value:'dismissed',label:'Dismissed'},
    {value:'all',label:'All'},
  ];

  return (
    <div style={{padding:32}}>
      <PageHeader title="Safety & Moderation"
        subtitle="Flagged activity, user reports, and content moderation" />

      {/* Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',
        gap:16,marginBottom:24}}>
        <StatCard label="Open Alerts" value={stats.open||0}
          icon="🚨" color="#ef4444" borderColor="#ef4444"
          sub={stats.open > 0 ? 'Requires attention' : 'All clear'} />
        <StatCard label="Under Review" value={stats.reviewed||0}
          icon="👁" color="#f97316" borderColor="#f97316" />
        <StatCard label="Flagged Accounts" value={stats.flagged||0}
          icon="🚩" color="#a855f7" borderColor="#a855f7" />
        <StatCard label="Moderation Queue"
          value={(stats.open||0) + (stats.flagged||0)}
          icon="📋" color={ACCENT} borderColor={ACCENT}
          sub="Open + Flagged items" />
      </div>

      {/* Moderation Queue Alert */}
      {(stats.open > 0) && (
        <div style={{padding:'16px 20px',background:'rgba(239,68,68,0.04)',
          border:'1px solid rgba(239,68,68,0.1)',borderRadius:14,
          marginBottom:20,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div style={{display:'flex',alignItems:'center',gap:14}}>
            <div style={{width:40,height:40,borderRadius:10,
              background:'rgba(239,68,68,0.1)',display:'flex',
              alignItems:'center',justifyContent:'center',fontSize:18}}>🚨</div>
            <div>
              <div style={{fontSize:14,fontWeight:700,color:'#ef4444'}}>
                {stats.open} Alert{stats.open !== 1 ? 's' : ''} Pending Review
              </div>
              <div style={{fontSize:12,color:'rgba(255,255,255,0.35)',marginTop:2}}>
                Review and action these alerts to maintain platform safety
              </div>
            </div>
          </div>
          <GoldBtn small onClick={() => setFilter('open')}>
            View Open
          </GoldBtn>
        </div>
      )}

      {/* Status filter tabs */}
      <TabButtons tabs={STATUS_TABS} active={filter} onChange={setFilter} />

      {/* Table */}
      <div style={{background:'#1A2332',border:'1px solid rgba(255,255,255,0.06)',
        borderRadius:16,overflow:'hidden',marginBottom:16}}>
        <Table columns={COLUMNS} data={alerts}
          loading={loading && alerts.length===0}
          onRowClick={setSelected} />
      </div>

      {hasMore && !loading && (
        <div style={{textAlign:'center'}}>
          <GoldBtn onClick={() => loadAlerts(false)}>Load More</GoldBtn>
        </div>
      )}
      {loading && alerts.length > 0 && <Spinner />}

      {/* Detail Modal */}
      {selected && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',
          display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,
          backdropFilter:'blur(6px)'}}>
          <style>{`
            @keyframes modalIn{from{opacity:0;transform:scale(0.95) translateY(10px)}to{opacity:1;transform:scale(1) translateY(0)}}
            .safety-modal{animation:modalIn 0.25s ease}
          `}</style>
          <div className="safety-modal" style={{background:'#1A2332',
            border:'1px solid rgba(0,135,81,0.2)',
            borderRadius:20,width:560,maxHeight:'85vh',overflow:'auto',padding:32,
            boxShadow:'0 24px 80px rgba(0,0,0,0.5)'}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:24}}>
              <div style={{display:'flex',alignItems:'center',gap:12}}>
                <div style={{width:44,height:44,borderRadius:12,
                  background:'rgba(239,68,68,0.08)',display:'flex',
                  alignItems:'center',justifyContent:'center',fontSize:20,
                  border:'1px solid rgba(239,68,68,0.15)'}}>
                  {ALERT_ICONS[selected.alertType] || ALERT_ICONS.default}
                </div>
                <div>
                  <div style={{fontFamily:'Syne',fontWeight:800,fontSize:17}}>
                    Alert Detail
                  </div>
                  <div style={{fontSize:12,color:'rgba(255,255,255,0.35)',marginTop:2}}>
                    {selected.alertType?.replace(/_/g,' ')}
                  </div>
                </div>
              </div>
              <button onClick={() => setSelected(null)}
                style={{background:'rgba(255,255,255,0.05)',border:'none',
                  color:'rgba(255,255,255,0.4)',fontSize:18,cursor:'pointer',
                  width:36,height:36,borderRadius:10,display:'flex',
                  alignItems:'center',justifyContent:'center'}}>✕</button>
            </div>

            {/* Status badge */}
            <div style={{marginBottom:20}}>
              <Badge label={selected.status} color={selected.status} />
            </div>

            {/* Alert info */}
            <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:24}}>
              {[
                ['Type',    selected.alertType?.replace(/_/g,' ')],
                ['User',    selected.email || '—'],
                ['UID',     selected.uid],
                ['Country', selected.country || '—'],
                ['Tier',    selected.tier || 'free'],
                ['Time',    fmtDate(selected.createdAt)],
              ].map(([label,val]) => (
                <div key={label} style={{display:'flex',justifyContent:'space-between',
                  padding:'11px 14px',background:'#0F1419',
                  borderRadius:10,border:'1px solid rgba(255,255,255,0.04)'}}>
                  <span style={{fontSize:12,color:'rgba(255,255,255,0.35)',
                    textTransform:'uppercase',letterSpacing:1}}>{label}</span>
                  <span style={{fontSize:13,fontFamily:'DM Mono',
                    color:'rgba(255,255,255,0.8)',wordBreak:'break-all',
                    maxWidth:300,textAlign:'right'}}>{String(val||'—')}</span>
                </div>
              ))}
            </div>

            {/* Meta / preview */}
            {selected.meta?.preview && (
              <div style={{padding:16,background:'rgba(239,68,68,0.04)',
                border:'1px solid rgba(239,68,68,0.1)',borderRadius:12,marginBottom:20}}>
                <div style={{fontSize:11,color:'rgba(255,255,255,0.3)',
                  marginBottom:8,textTransform:'uppercase',letterSpacing:1.5,fontWeight:600}}>
                  Message Preview
                </div>
                <div style={{fontSize:13,color:'rgba(255,255,255,0.7)',
                  fontStyle:'italic',lineHeight:1.6,
                  padding:'10px 14px',background:'rgba(0,0,0,0.2)',borderRadius:8}}>
                  "{selected.meta.preview}"
                </div>
              </div>
            )}

            {/* Actions */}
            <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
              {selected.status !== 'reviewed' && (
                <GoldBtn onClick={() => updateStatus(selected.id,'reviewed')} small>
                  Mark Reviewed
                </GoldBtn>
              )}
              {selected.status !== 'flagged' && (
                <GoldBtn onClick={() => updateStatus(selected.id,'flagged')} small danger>
                  Flag Account
                </GoldBtn>
              )}
              <GoldBtn onClick={() => updateStatus(selected.id,'dismissed')} small danger>
                Dismiss
              </GoldBtn>
              {selected.uid && (
                <>
                  <GoldBtn onClick={() => blockUser(selected.uid)} small danger>
                    Block User
                  </GoldBtn>
                  <GoldBtn onClick={() => unblockUser(selected.uid)} small>
                    Unblock User
                  </GoldBtn>
                </>
              )}
              <a href={`mailto:${selected.email}`}
                style={{padding:'7px 16px',background:'rgba(59,130,246,0.08)',
                  border:'1px solid rgba(59,130,246,0.15)',borderRadius:10,
                  color:'#3b82f6',fontSize:12,fontWeight:600,textDecoration:'none',
                  display:'flex',alignItems:'center',transition:'all 0.2s'}}>
                Email User
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
